import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { apiFetch } from '../../lib/api';

/** Matches open panel width (+ tab) so extended absolute sheet does not shift horizontally vs compact. */
export const NOTES_EXTENDED_SHELL_W =
  'w-[calc(min(max(14rem,min(22rem,calc(100vw-2rem))),calc(100vw-1.25rem))+2.5rem)] shrink-0';

const NOTES_EXTENDED_CLAMP_GAP_PX = 6;

function notesExtendedTargetShellWidthPx(vw: number): number {
  const rem = 16;
  const a = Math.min(22 * rem, vw - 32);
  const b = Math.max(14 * rem, a);
  const c = Math.min(b, vw - 20);
  return c + 2.5 * rem;
}

function notesExtendedHeightPx(vh: number): number {
  const rem = 16;
  return Math.min(Math.max(26 * rem, vh * 0.58), Math.min(42 * rem, vh - 64));
}

function IconChevronDown({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

const notesTabRailChrome =
  'w-[2.5rem] shrink-0 border border-white/20 font-display text-white shadow-md ring-1 ring-black/15';

const notesTabRailPink = `${notesTabRailChrome} bg-[var(--header-shell-bg)]/92`;

const notesTabRailBlue = `${notesTabRailChrome} bg-[var(--home-miami-blue)]/90`;

const notesRailGlassOpen =
  'rounded-r-2xl border border-white/[0.08] bg-[var(--home-zone-dark)]/50 shadow-[0_48px_100px_-50px_rgb(0_0_0/0.85)] ring-1 ring-white/[0.04] backdrop-blur-sm';

/** Staff notes rail — shared by marketing leads table and CRM contacts (PATCH body uses `notesBodyKey`). */
export function CrmNotesRailTab({
  recordId,
  label,
  notes,
  token,
  patchUrl,
  notesBodyKey,
  onSaved,
  notesOpen,
  onNotesOpenChange,
  tabVariant,
  heightExpanded,
  onHeightExpandedChange,
  containerRef,
  notesRailRowElsRef,
  railRowId,
  placeholder = 'Staff notes…',
  ariaRegionLabel = 'Staff notes',
}: {
  recordId: string;
  label: string;
  notes: string | null;
  token: string;
  patchUrl: string;
  notesBodyKey: string;
  onSaved: (id: string, nextNotes: string | null) => void;
  notesOpen: boolean;
  onNotesOpenChange: (open: boolean) => void;
  tabVariant: 'pink' | 'blue';
  heightExpanded: boolean;
  onHeightExpandedChange: (expanded: boolean) => void;
  containerRef: RefObject<HTMLDivElement | null>;
  notesRailRowElsRef: RefObject<Map<string, HTMLDivElement>>;
  railRowId: string;
  placeholder?: string;
  ariaRegionLabel?: string;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const taId = `crm-row-notes-${recordId}`;
  const panelId = `crm-row-notes-panel-${recordId}`;
  const valueRef = useRef(value);
  const serverNotesRef = useRef(notes);
  const autosaveTimerRef = useRef<number | null>(null);
  const [extendedFixedRect, setExtendedFixedRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  valueRef.current = value;
  serverNotesRef.current = notes;

  const persistNotes = useCallback(async (): Promise<void> => {
    const next = valueRef.current.trim() || null;
    const prev = (serverNotesRef.current ?? '').trim() || null;
    if (next === prev) return;
    setBusy(true);
    try {
      await apiFetch(patchUrl, {
        method: 'PATCH',
        token,
        body: JSON.stringify({ [notesBodyKey]: next }),
      });
      onSaved(recordId, next);
      serverNotesRef.current = next;
    } finally {
      setBusy(false);
    }
  }, [recordId, token, patchUrl, notesBodyKey, onSaved]);

  const scheduleAutosave = useCallback(() => {
    if (autosaveTimerRef.current != null) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      void persistNotes();
    }, 650);
  }, [persistNotes]);

  useEffect(() => {
    if (!notesOpen || !heightExpanded) setExtendedFixedRect(null);
  }, [notesOpen, heightExpanded]);

  useLayoutEffect(() => {
    if (!notesOpen || !heightExpanded) {
      return;
    }
    function measure(): void {
      const le = containerRef.current;
      const rr = notesRailRowElsRef.current?.get(railRowId);
      if (!le || !rr) {
        setExtendedFixedRect(null);
        return;
      }
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const leadsR = le.getBoundingClientRect();
      const rowR = rr.getBoundingClientRect();
      const targetW = notesExtendedTargetShellWidthPx(vw);
      const sheetRight = rowR.right;
      const minLeft = leadsR.right + NOTES_EXTENDED_CLAMP_GAP_PX;
      const left = Math.max(minLeft, sheetRight - targetW);
      const width = targetW;
      const height = notesExtendedHeightPx(vh);
      setExtendedFixedRect({ top: rowR.top, left, width, height });
    }
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    const ro = new ResizeObserver(measure);
    const le0 = containerRef.current;
    const rr0 = notesRailRowElsRef.current?.get(railRowId);
    if (le0) ro.observe(le0);
    if (rr0) ro.observe(rr0);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      ro.disconnect();
    };
  }, [notesOpen, heightExpanded, containerRef, notesRailRowElsRef, railRowId]);

  useEffect(() => {
    if (!notesOpen) return;
    setValue(notes ?? '');
  }, [notesOpen, recordId, notes]);

  useEffect(() => {
    if (!notesOpen) return;
    scheduleAutosave();
    return () => {
      if (autosaveTimerRef.current != null) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [value, notesOpen, scheduleAutosave]);

  useEffect(() => {
    if (!notesOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (autosaveTimerRef.current != null) {
          window.clearTimeout(autosaveTimerRef.current);
          autosaveTimerRef.current = null;
        }
        onHeightExpandedChange(false);
        onNotesOpenChange(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [notesOpen, onHeightExpandedChange, onNotesOpenChange]);

  useEffect(() => {
    if (!notesOpen) return;
    const raf = window.requestAnimationFrame(() => {
      const el = document.getElementById(taId) as HTMLTextAreaElement | null;
      el?.focus();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [notesOpen, taId]);

  const prevNotesOpenRef = useRef(notesOpen);
  useEffect(() => {
    if (prevNotesOpenRef.current && !notesOpen) {
      if (autosaveTimerRef.current != null) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      void persistNotes();
    }
    prevNotesOpenRef.current = notesOpen;
  }, [notesOpen, persistNotes]);

  useEffect(
    () => () => {
      if (autosaveTimerRef.current != null) window.clearTimeout(autosaveTimerRef.current);
    },
    [],
  );

  function handleToggle() {
    if (notesOpen) {
      if (autosaveTimerRef.current != null) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      onHeightExpandedChange(false);
      onNotesOpenChange(false);
    } else {
      onNotesOpenChange(true);
    }
  }

  const notesTabRailClass = tabVariant === 'pink' ? notesTabRailPink : notesTabRailBlue;

  const notesTabHeightToggle =
    'mx-auto mb-2 inline-flex items-center justify-center rounded-md border-0 bg-transparent p-1 text-white/95 transition-colors duration-200 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/45 focus-visible:ring-offset-0';

  const extendedSheetShadow = 'shadow-[0_32px_80px_-30px_rgb(0_0_0/0.78)] ring-1 ring-black/15';
  const extendedUsesFixed = Boolean(notesOpen && heightExpanded && extendedFixedRect);

  return (
    <div className="relative flex h-full min-h-0 justify-end overflow-visible">
      <div
        className={[
          'flex flex-row-reverse items-stretch overflow-hidden transition-[box-shadow,background-color,height,min-height,width] duration-200 ease-out',
          notesOpen ? notesRailGlassOpen : '',
          notesOpen && heightExpanded && extendedUsesFixed
            ? `fixed z-[100] ${extendedSheetShadow}`
            : notesOpen && heightExpanded
              ? `absolute right-0 top-0 left-auto z-10 h-[clamp(26rem,58vh,min(42rem,calc(100vh-4rem)))] ${NOTES_EXTENDED_SHELL_W} ${extendedSheetShadow}`
              : notesOpen
                ? 'relative h-full max-h-full min-h-0'
                : 'relative min-h-[2.75rem]',
        ].join(' ')}
        style={
          extendedUsesFixed && extendedFixedRect
            ? {
                top: extendedFixedRect.top,
                left: extendedFixedRect.left,
                width: extendedFixedRect.width,
                height: extendedFixedRect.height,
              }
            : undefined
        }
      >
        {notesOpen ? (
          <div
            className={`${notesTabRailClass} flex h-full max-h-full min-h-0 flex-col items-stretch overflow-hidden self-stretch rounded-l-none rounded-r-2xl border-l border-white/15 border-r-0`}
          >
            <button
              type="button"
              onClick={() => handleToggle()}
              className="flex min-h-0 flex-1 flex-col items-center gap-2 border-0 bg-transparent px-1 pt-4 pb-2 text-center text-inherit shadow-none ring-0 transition hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/45"
              aria-expanded={notesOpen}
              aria-controls={panelId}
              title="Hide notes"
            >
              <span className="select-none text-[0.45rem] font-bold uppercase leading-tight tracking-[0.12em] [writing-mode:vertical-rl]">
                Notes
              </span>
            </button>
            <button
              type="button"
              className={notesTabHeightToggle}
              aria-expanded={heightExpanded}
              aria-label={heightExpanded ? 'Collapse notes height' : 'Expand notes height'}
              title={heightExpanded ? 'Compact height' : 'Taller editor'}
              onClick={(e) => {
                e.stopPropagation();
                onHeightExpandedChange(!heightExpanded);
              }}
            >
              <IconChevronDown
                className={`h-[1.125rem] w-[1.125rem] shrink-0 transition-transform duration-200 ease-out ${heightExpanded ? '-rotate-180' : 'rotate-0'}`}
              />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => handleToggle()}
            className={`${notesTabRailClass} flex h-full min-h-[2.75rem] flex-col items-center justify-center gap-1 rounded-r-xl px-1.5 py-2 text-center transition hover:brightness-110`}
            aria-expanded={notesOpen}
            aria-controls={panelId}
            title="Show notes"
          >
            <span className="select-none text-[0.45rem] font-bold uppercase leading-tight tracking-[0.12em] [writing-mode:vertical-rl]">
              Notes
            </span>
          </button>
        )}
        <div
          id={panelId}
          className={`flex h-full max-h-full min-h-0 flex-col overflow-hidden border-white/10 transition-[width,opacity] duration-200 ease-out ${
            notesOpen
              ? extendedUsesFixed
                ? 'min-h-0 min-w-0 w-auto flex-1 border-r opacity-100'
                : 'w-[min(22rem,calc(100vw-2rem))] min-w-[14rem] max-w-[calc(100vw-1.25rem)] border-r opacity-100'
              : 'w-0 border-0 opacity-0'
          }`}
          role="region"
          aria-label={`${ariaRegionLabel} for ${label}`}
        >
          {notesOpen ? (
            <div className="relative flex h-full min-h-0 w-full overflow-hidden px-3 py-2">
              <label htmlFor={taId} className="sr-only">
                {ariaRegionLabel} (internal)
              </label>
              <textarea
                id={taId}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onBlur={() => {
                  if (autosaveTimerRef.current != null) {
                    window.clearTimeout(autosaveTimerRef.current);
                    autosaveTimerRef.current = null;
                  }
                  void persistNotes();
                }}
                placeholder={placeholder}
                className={`min-h-0 w-full flex-1 resize-none overflow-y-auto rounded-lg border border-white/12 bg-black/25 p-2 pb-8 text-sm text-[var(--home-text-on-dark)] placeholder:text-[var(--home-muted-on-dark)] shadow-inner shadow-black/20 outline-none backdrop-blur-sm focus:border-[var(--home-miami-blue)] focus:ring-2 focus:ring-[var(--home-miami-blue)]/30`}
              />
              <div className="pointer-events-none absolute bottom-2 left-3 right-3 flex items-end">
                <p className="min-h-4 w-full text-[10px] text-[var(--home-muted-on-dark)]" aria-live="polite">
                  {busy ? 'Saving…' : '\u00a0'}
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
